import Cookies from 'js-cookie';
import { getBaseUrl } from '../utils/apiHelper';
import authService from './authService';

const API_URL = `${getBaseUrl()}/api/invoices`;

const invoiceService = {
    getInvoicesByUser: async (userId) => {
        const res = await authService.authenticatedFetch(`${API_URL}?userId=${userId}`, {
            method: "GET",
            headers: { 
                "Content-Type": "application/json"
            },
        });

        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.message || "Failed to fetch invoices");
        }

        console.log("✅ Received Invoices:", data.invoices); // Log received invoices
        return data.invoices;
    },

    getInvoiceById: async (invoiceId) => {
        try {
            const token = Cookies.get("token");
            const endpoint = `${API_URL}/${invoiceId}`; // Use invoiceId directly
            console.log(`Fetching invoice with endpoint: ${endpoint}`);
            const res = await fetch(endpoint, {
                method: "GET",
                headers: { 
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            const data = await res.json();
            if (!res.ok) {
                console.error(`❌ Failed to fetch invoice: ${data.message}`);
                throw new Error(data.message || "Failed to fetch invoice");
            }

            console.log("✅ Invoice fetched successfully:", data.invoice);
            return data.invoice;
        } catch (error) {
            console.error("❌ Error fetching invoice:", error.message);
            throw error;
        }
    },

    uploadInvoice: async (formData) => {
        const token = Cookies.get("token");
        const res = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
            body: formData,
        });

        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.message || "Failed to upload invoice");
        }

        return data.invoice;
    },

    updateInvoice: async (invoiceId, updatedData) => {
        try {
            const token = Cookies.get("token");
            const endpoint = `${API_URL}/${invoiceId}`;
            console.log(`Updating invoice with endpoint: ${endpoint}`);

            // Exclude the pdfFile field from the payload
            const { pdfFile, ...filteredData } = updatedData;

            // Log the presence of pdfFile and the filtered payload
            if (pdfFile) {
                console.warn("⚠️ The pdfFile field was excluded from the payload.");
            }
            console.log("📄 Payload being sent (excluding pdfFile):", filteredData);

            const res = await fetch(endpoint, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(filteredData), // Send filtered data without pdfFile
            });

            // Read the response body only once
            const rawResponse = await res.text();
            let data;
            try {
                data = JSON.parse(rawResponse); // Parse the response as JSON
            } catch (error) {
                console.error("❌ Failed to parse JSON response:", rawResponse);
                throw new Error("Unexpected response format from server.");
            }

            if (!res.ok) {
                console.error(`❌ Failed to update invoice: ${data.message}`);
                throw new Error(data.message || "Failed to update invoice");
            }

            console.log("✅ Invoice updated successfully:", data.invoice);
            return data.invoice;
        } catch (error) {
            console.error("❌ Error updating invoice:", error.message);
            throw error;
        }
    },

    deleteInvoice: async (invoiceId) => {
        const token = Cookies.get("token");
        const res = await fetch(`${API_URL}/${invoiceId}`, { // Use invoice ID directly in the URL
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();
        if (!res.ok) {
            console.error(`❌ Failed to delete invoice with ID: ${invoiceId}`, data.message);
            throw new Error(data.message || "Failed to delete invoice");
        }

        console.log(`✅ Invoice with ID: ${invoiceId} deleted successfully.`);
        return data.message;
    },

    fetchAllInvoices: async () => {
        const token = Cookies.get("token");
        const res = await fetch(`${API_URL}/all`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const errorData = await res.json();
            console.error("❌ Failed to fetch all invoices:", errorData);
            throw new Error(errorData.message || "Failed to fetch all invoices");
        }

        const data = await res.json();
        console.log("✅ Fetched all invoices:", data.invoices); // Log received invoices
        return data.invoices;
    },    toggleReviewStatus: async (invoiceId) => {
        const token = Cookies.get("token"); // Retrieve the user's auth token
        const userId = Cookies.get("userId"); // Retrieve User ID from cookies
        
        try {
            console.log(`📑 Toggling review status for invoice ID: ${invoiceId}`);
            console.log("🔑 Authentication details:", {
                token: token ? `${token.substring(0, 10)}...` : "Not found",
                userId: userId || "Not found"
            });
            
            const response = await fetch(`${API_URL}/${invoiceId}/toggle-review`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-User-ID": userId || ""
                },
            });
            
            console.log('📥 Response status:', response.status, response.statusText);

            if (!response.ok) {
                const errorData = await response.json();
                console.error(`❌ Failed to toggle review status: ${errorData.message}`);
                throw new Error(errorData.message || `Error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            console.log("✅ Review status toggled successfully:", data.invoice);
            return data.invoice; // Return the updated invoice
        } catch (error) {
            console.error("❌ Error toggling review status:", error);
            throw error;
        }
    },

    getInvoicesByClient: async (clientId, page = null, limit = null) => {
        try {
            const token = Cookies.get("token");
            let endpoint = `${API_URL}/client/${clientId}`;
            
            // Add pagination parameters if provided
            if (page !== null && limit !== null) {
                const validPage = Math.max(1, parseInt(page) || 1);
                const validLimit = Math.max(1, Math.min(50, parseInt(limit) || 20));
                endpoint += `?page=${validPage}&limit=${validLimit}`;
                console.log(`📄 [PAGINATION DEBUG] Fetching paginated client invoices: ${endpoint}`);
            } else {
                console.log(`📄 Fetching all invoices for client with endpoint: ${endpoint}`);
            }
            
            const res = await fetch(endpoint, {
                method: "GET",
                headers: { 
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            const data = await res.json();
            if (!res.ok) {
                console.error(`❌ Failed to fetch invoices for client: ${data.message}`);
                throw new Error(data.message || "Failed to fetch invoices for client");
            }

            // Check if we received paginated response or just invoices array
            if (data.hasOwnProperty('invoices') && data.hasOwnProperty('total') && data.hasOwnProperty('page')) {
                console.log(`✅ Client's paginated invoices fetched successfully: page ${data.page} of ${data.totalPages}`);
                return data; // Return the full pagination object
            } else {
                console.log("✅ Client's invoices fetched successfully:", data.invoices || data);
                const invoices = data.invoices || data;
                return {
                    invoices: invoices,
                    total: invoices.length,
                    page: 1,
                    totalPages: 1
                };
            }
        } catch (error) {
            console.error("❌ Error fetching client's invoices:", error.message);
            throw error;
        }
    },

    getInvoicesByClientWithoutPDF: async (clientId) => {
        try {
            const token = Cookies.get("token");
            const endpoint = `${API_URL}/client/${clientId}/no-pdf`;
            console.log(`📄 Fetching invoices (without PDF) for client ID: ${clientId}`);
            
            const res = await fetch(endpoint, {
                method: "GET",
                headers: { 
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            const data = await res.json();
            if (!res.ok) {
                console.error(`❌ Failed to fetch invoices for client: ${data.message}`);
                throw new Error(data.message || "Failed to fetch invoices for client");
            }

            console.log(`✅ Successfully fetched ${data.invoices.length} invoices for client (without PDF data)`);
            return data.invoices;
        } catch (error) {
            console.error("❌ Error fetching client's invoices without PDF:", error.message);
            throw error;
        }
    },

    updateComment: async (invoiceId, commentText) => {
        try {
            console.log(`📄 Updating comment for invoice ID: ${invoiceId}...`);
            const token = Cookies.get("token");
            
            // Try with 'updateComment' endpoint instead of 'comment'
            const response = await fetch(`${API_URL}/${invoiceId}/updateComment`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ comment: commentText }),
            });

            // Handle non-JSON responses for better error messaging
            if (!response.ok) {
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.includes("application/json")) {
                    const data = await response.json();
                    throw new Error(data.message || "Failed to update comment for invoice.");
                } else {
                    const text = await response.text();
                    console.error("Non-JSON error response:", text);
                    throw new Error(`Server error (${response.status}): The comment endpoint may not be implemented correctly.`);
                }
            }

            const data = await response.json();
            console.log("✅ Comment updated for invoice successfully:", data.invoice);
            return data.invoice;
        } catch (error) {
            console.error("❌ Error updating comment for invoice:", error.message);
            throw error;
        }
    },

    getPaginatedInvoices: async (page = 1, limit = 20, clientId = null) => {
        try {
            const token = Cookies.get("token");
            
            // Ensure page and limit are valid numbers and match format used in working email pagination
            const validPage = Math.max(1, parseInt(page) || 1);
            const validLimit = Math.max(1, Math.min(50, parseInt(limit) || 20)); // Cap at 50 items max
            
            // Build endpoint URL with query parameters that match the backend expectations
            let endpoint;
            
            if (clientId) {
                // For client-specific requests, follow the same pattern as the working email pagination
                endpoint = `${API_URL}/client/${clientId}?page=${validPage}&limit=${validLimit}`;
            } else {
                // Use the /all endpoint with pagination=true flag for clarity
                endpoint = `${API_URL}/all?page=${validPage}&limit=${validLimit}`;
            }
            
            console.log(`📄 [PAGINATION DEBUG] Fetching paginated invoices: ${endpoint}`);
            console.log(`📄 [PAGINATION DEBUG] Using page=${validPage}, limit=${validLimit}${clientId ? ', for clientId=' + clientId : ''}`);
            
            const res = await fetch(endpoint, {
                method: "GET",
                headers: { 
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            console.log(`📄 [PAGINATION DEBUG] Response status: ${res.status} ${res.statusText}`);
            
            // Handle potential non-JSON responses safely
            let data;
            let rawText = null;
            
            try {
                // First get the text
                rawText = await res.text();
                // Then try to parse it as JSON
                data = JSON.parse(rawText);
            } catch (error) {
                console.error(`❌ [PAGINATION DEBUG] Failed to parse JSON response: ${error.message}`);
                if (rawText) {
                    console.error(`❌ [PAGINATION DEBUG] Raw response: ${rawText.substring(0, 200)}...`);
                }
                throw new Error(`Failed to parse server response as JSON. Status: ${res.status}`);
            }
            
            if (!res.ok) {
                console.error(`❌ [PAGINATION DEBUG] Failed to fetch paginated invoices: ${JSON.stringify(data)}`);
                throw new Error(data.message || "Failed to fetch paginated invoices");
            }

            // Get invoice data from response
            const invoices = data.invoices || [];
            
            // Use the pagination metadata from the backend, or create it if not provided
            const total = data.total || invoices.length;
            const totalPages = data.totalPages || Math.ceil(total / validLimit) || 1;
            const currentPage = data.page || validPage;
            
            // For safety, limit the number of invoices if the backend doesn't do it
            const limitedInvoices = invoices.slice(0, validLimit);

            console.log(`✅ [PAGINATION DEBUG] Success! Received page ${currentPage} of ${totalPages}`);
            console.log(`📄 [PAGINATION DEBUG] Total items: ${total}, Showing: ${limitedInvoices.length} invoices`);
            
            // Return standardized pagination response matching email pagination format
            return {
                invoices: limitedInvoices,
                total: total,
                page: currentPage,
                totalPages: totalPages,
                pageSize: validLimit
            };
        } catch (error) {
            console.error(`❌ [PAGINATION DEBUG] Error fetching paginated invoices: ${error.message}`);
            console.error(error);
            throw error;
        }
    },
};

export default invoiceService;
